import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from "@angular/router";
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  iconoirLogIn,
  iconoirUserPlus,
  iconoirCheckCircle,
  iconoirViewGrid,
  iconoirChatLines,
  iconoirMapPin,
  iconoirFlash,
  iconoirCreditCardSlash
} from '@ng-icons/iconoir';
import { AuthService } from "../../../core/auth/auth.service";
import { LoginByEmailComponent } from "./__partials/login-by-email/login-by-email.component";
import { SignUpFlowComponent } from "./__partials/sign-up-flow/sign-up-flow.component";
import { ThemeToggleComponent } from "../../../components/ui/buttons/theme-toggle/theme-toggle.component";
import { authMascot } from "../auth-mascots";
import { ConsentBannerComponent } from "../../../components/ui/consent-banner/consent-banner.component";
import { GtmService } from "../../../core/tracking/gtm.service";
import { AudienceService } from "../../../core/tracking/audience.service";

@Component({
  selector: 'app-sign-in',
  imports: [
    RouterLink,
    LoginByEmailComponent,
    SignUpFlowComponent,
    NgIconComponent,
    ThemeToggleComponent,
    ConsentBannerComponent
  ],
  providers: [
    provideIcons({
      iconoirLogIn,
      iconoirUserPlus,
      iconoirCheckCircle,
      iconoirViewGrid,
      iconoirChatLines,
      iconoirMapPin,
      iconoirFlash,
      iconoirCreditCardSlash
    })
  ],
  templateUrl: './sign-in.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sign-in.component.scss',
  // Suivi des champs pour Google Tag Manager (MOKA-319) : id du champ seulement, après consentement.
  host: {
    '(focusin)': 'gtm.fieldEvent("focus", $event.target, formId())',
    '(change)': 'gtm.fieldEvent("change", $event.target, formId())',
  }
})
export class SignInComponent {

  public readonly authService = inject(AuthService);
  public readonly gtm = inject(GtmService);
  /** Mesure d'audience exemptée (MOKA-355) : démarre avec la page de connexion / inscription. */
  private readonly audience = inject(AudienceService);
  public readonly activeTab = signal<'signIn' | 'signUp'>('signIn');
  /** Formulaire affiché, transmis avec chaque événement de champ. */
  public readonly formId = computed(() => (this.activeTab() === 'signUp' ? 'inscription' : 'connexion'));

  /**
   * Email à valider passé par le lien de l'email de confirmation
   * (/sign-in?verify=<email>) : ouvre directement l'écran de saisie du code.
   */
  public readonly verifyEmail = signal<string | null>(null);

  /** Poses de Koffi (catalogue MOKKIE_POSES) : une par onglet. */
  public readonly mascots = {
    salut: authMascot('salut'),
    guide: authMascot('guide')
  };

  constructor() {
    const route = inject(ActivatedRoute);

    // /sign-up ouvre directement l'onglet Inscription (data.activeTab)
    if (route.snapshot.data['activeTab'] === 'signUp') {
      this.activeTab.set('signUp');
    }

    const verify = route.snapshot.queryParamMap.get('verify');
    if (verify) {
      this.verifyEmail.set(verify);
      this.activeTab.set('signUp');
    }
  }

  /**
   * Arguments produit du panneau de gauche. Titre + détail plutôt qu'une simple
   * puce : ce sont les seuls éléments de vente de la page, ils méritent d'être
   * lisibles (retour William, 23/09/2026).
   */
  public readonly features = [
    {
      icon: 'iconoirViewGrid',
      title: 'Tout au même endroit',
      detail: 'Projets, RH, inventaire, calendrier et réservation de salles.'
    },
    {
      icon: 'iconoirChatLines',
      title: 'Assistant intégré',
      detail: 'Créez et suivez vos tâches à la voix ou à l\'écrit.'
    },
    {
      icon: 'iconoirMapPin',
      title: 'Hébergé en France',
      detail: 'Vos données restent sur une infrastructure française.'
    },
    {
      icon: 'iconoirFlash',
      title: 'Prêt en deux minutes',
      detail: 'Validation par email instantanée, aucune installation.'
    }
  ];

}
